/**
 * ui/storyboard-ui.js
 * Quản lý Kho Nhân Vật (Character Roster), Kịch bản Phân cảnh (Storyboard Shots) và Dự Án VAS.
 */

import { AVATAR_PRESETS } from '../../avatars/avatar-presets.js';
import { ANIMAL_PRESETS } from '../../avatars/animal-presets.js';
import { StoryboardDirector } from '../../studio/storyboard-director.js';

import { defaultSfx } from '../../audio/sfx.js';
import { setTimeOfDay } from '../scene-setup.js';
import { buildShotCardHtml } from './storyboard-template.js';
import { getOptionLabel } from './labels.js';
import { escapeHtml } from '../../core/html.js';
import { spawnProjectPets, initProjectActionListeners } from './storyboard-project-actions.js';

export function formatTime(sec) {
  const s = Math.floor(sec);
  const m = Math.floor(s / 60);
  const rem = s % 60;
  return `${String(m).padStart(2, '0')}:${String(rem).padStart(2, '0')}`;
}

export function initStoryboardUI(state, handlers = {}) {
  const { characterManager, projectManager, emotionEffectsManager } = state;

  function updateStoryboardDirector() {
    if (!state.storyboardDirector) {
      state.storyboardDirector = new StoryboardDirector(
        state.scene,
        state.camera,
        state.controls,
        characterManager,
        emotionEffectsManager
      );
    }
    state.storyboardDirector.loadStoryboard(state.currentProject.storyboard);
    const slider = document.getElementById('drama-time-slider');
    const totalTimeEl = document.getElementById('drama-total-time');
    if (slider) slider.max = state.storyboardDirector.totalDuration;
    if (totalTimeEl) totalTimeEl.textContent = formatTime(state.storyboardDirector.totalDuration);
  }

  function previewShot(idx) {
    if (!state.currentProject.storyboard || !state.currentProject.storyboard[idx]) return;
    if (state.currentMode !== 'drama' && state.currentMode !== 'city' && typeof handlers.switchMode === 'function') {
      handlers.switchMode('city', { keepPanel: true });
    }
    updateStoryboardDirector();
    let targetTime = 0;
    for (let i = 0; i < idx; i++) {
      targetTime += state.currentProject.storyboard[i].duration || 1;
    }
    targetTime += 0.05;
    state.storyboardDirector.seek(targetTime, true);

    const subtitleBox = document.getElementById('drama-subtitle-box');
    const speakerEl = document.getElementById('subtitle-speaker');
    const textEl = document.getElementById('subtitle-text');
    const shot = state.currentProject.storyboard[idx];
    if (subtitleBox && speakerEl && textEl && shot) {
      subtitleBox.style.display = 'block';
      speakerEl.textContent = shot.actorName || 'Diễn viên';
      textEl.textContent = shot.dialogue || '';
    }
  }

  function renderCharacterRoster() {
    const container = document.getElementById('char-roster-container');
    const countEl = document.getElementById('roster-count');
    if (!container) return;

    const characters = characterManager.getAllCharacters();
    if (countEl) countEl.textContent = characters.length;

    container.innerHTML = '';
    characters.forEach((char) => {
      const card = document.createElement('div');
      card.className = 'roster-card';
      if (
        state.mainAvatar &&
        state.mainAvatar.config &&
        (state.mainAvatar.config.name === char.name || state.mainAvatar.config.id === char.id)
      ) {
        card.classList.add('active');
      }

      const isBuiltIn = !!char.isBuiltIn;
      let detailsHtml = '';
      if (char.type === 'animal') {
        const spPreset = ANIMAL_PRESETS[char.speciesKey];
        const spName = spPreset?.name || char.speciesKey || 'Động vật';
        const varName = spPreset?.variants?.[char.variantKey]?.name || char.variantKey || 'Mặc định';
        detailsHtml = `🐾 Loài: ${escapeHtml(spName)} · 🎨 Biến thể: ${escapeHtml(varName)}`;
      } else {
        const outfitLabel = getOptionLabel('outfit', char.features?.outfit);
        const hairLabel = getOptionLabel('hairStyle', char.features?.hairStyle);
        detailsHtml = `👔 ${escapeHtml(outfitLabel)} · 💇 ${escapeHtml(hairLabel)}`;
      }

      card.innerHTML = `
        <div class="roster-header">
          <span class="roster-title">${escapeHtml(char.name)}</span>
          <span class="roster-badge ${isBuiltIn ? '' : 'custom'}">${isBuiltIn ? 'Mặc định' : 'Tùy biến'}</span>
        </div>
        <div class="roster-details">
          ${detailsHtml}
        </div>
        <div class="roster-actions">
          <button class="roster-btn primary btn-load-char" title="Mở nhân vật này vào studio">👁️ Xem & Sửa</button>
          <button class="roster-btn btn-clone-char" title="Nhân bản nhân vật">📋 Nhân bản</button>
          ${!isBuiltIn ? '<button class="roster-btn btn-delete-char" title="Xóa nhân vật">🗑️</button>' : ''}
        </div>
      `;

      card.querySelector('.btn-load-char').addEventListener('click', () => {
        const initParams = characterManager.toAvatarInitParams(char);
        state.currentPresetKey = initParams.presetKey;
        if (typeof handlers.setupStudioAvatar === 'function') {
          handlers.setupStudioAvatar(state.currentPresetKey, initParams.options);
        }
        if (typeof handlers.syncCustomizerChips === 'function') {
          handlers.syncCustomizerChips(initParams.options);
        }
        const nameInput = document.getElementById('input-current-char-name');
        if (nameInput) nameInput.value = char.name;
        renderCharacterRoster();
        if (typeof handlers.switchStudioPanel === 'function') {
          handlers.switchStudioPanel('customizer');
        }
      });

      card.querySelector('.btn-clone-char').addEventListener('click', () => {
        characterManager.duplicateCharacter(char.id);
        renderCharacterRoster();
      });

      const delBtn = card.querySelector('.btn-delete-char');
      if (delBtn) {
        delBtn.addEventListener('click', (e) => {
          e.stopPropagation();
          if (confirm(`Bạn có chắc muốn xóa nhân vật "${char.name}" không?`)) {
            characterManager.deleteCharacter(char.id);
            renderCharacterRoster();
          }
        });
      }

      container.appendChild(card);
    });
  }

  function renderStoryboardShots() {
    const container = document.getElementById('storyboard-shots-container');
    const durBadge = document.getElementById('storyboard-total-duration-badge');
    if (!container) return;

    const storyboard = state.currentProject.storyboard || [];
    const totalDur = storyboard.reduce((acc, s) => acc + (parseFloat(s.duration) || 1), 0);
    if (durBadge) durBadge.textContent = `Tổng: ${totalDur.toFixed(1)}s`;

    const allChars = characterManager.getAllCharacters();

    container.innerHTML = '';
    storyboard.forEach((shot, idx) => {
      const card = document.createElement('div');
      card.className = 'shot-card';
      if (state.storyboardDirector && state.storyboardDirector.currentShotIndex === idx) {
        card.classList.add('active');
      }

      card.innerHTML = buildShotCardHtml(shot, idx, storyboard.length, allChars);

      card.querySelectorAll('.shot-select').forEach((sel) => {
        sel.title = sel.options[sel.selectedIndex]?.text || '';
        sel.addEventListener('change', () => {
          sel.title = sel.options[sel.selectedIndex]?.text || '';
        });
      });

      const titleIn = card.querySelector('.shot-title-input');
      titleIn.addEventListener('input', () => {
        shot.title = titleIn.value;
        projectManager.saveDraft(state.currentProject);
      });

      const durIn = card.querySelector('.shot-duration-input');
      durIn.addEventListener('change', () => {
        shot.duration = Math.max(0.5, parseFloat(durIn.value) || 1);
        projectManager.saveDraft(state.currentProject);
        updateStoryboardDirector();
        renderStoryboardShots();
      });

      const actorSel = card.querySelector('.sel-shot-actor');
      actorSel.addEventListener('change', () => {
        shot.actorId = actorSel.value;
        const found = allChars.find((c) => c.id === shot.actorId);
        if (found) shot.actorName = found.name;
        projectManager.saveDraft(state.currentProject);
        updateStoryboardDirector();
      });

      const spawnSel = card.querySelector('.sel-shot-spawn');
      spawnSel.addEventListener('change', () => {
        shot.spawnPoint = spawnSel.value;
        projectManager.saveDraft(state.currentProject);
        updateStoryboardDirector();
      });

      const actSel = card.querySelector('.sel-shot-action');
      actSel.addEventListener('change', () => {
        shot.action = actSel.value;
        projectManager.saveDraft(state.currentProject);
        updateStoryboardDirector();
      });

      const exprSel = card.querySelector('.sel-shot-expr');
      exprSel.addEventListener('change', () => {
        shot.expression = exprSel.value;
        projectManager.saveDraft(state.currentProject);
        updateStoryboardDirector();
      });

      const emotSel = card.querySelector('.sel-shot-emotion');
      emotSel.addEventListener('change', () => {
        shot.emotionFx = emotSel.value;
        projectManager.saveDraft(state.currentProject);
        updateStoryboardDirector();
      });

      const camSel = card.querySelector('.sel-shot-camera');
      camSel.addEventListener('change', () => {
        if (!shot.camera) shot.camera = {};
        shot.camera.preset = camSel.value;
        projectManager.saveDraft(state.currentProject);
        updateStoryboardDirector();
      });

      const diaIn = card.querySelector('.shot-dialogue-input');
      diaIn.addEventListener('input', () => {
        shot.dialogue = diaIn.value;
        projectManager.saveDraft(state.currentProject);
        updateStoryboardDirector();
      });

      card.querySelector('.btn-shot-up').addEventListener('click', () => {
        if (idx > 0) {
          const temp = storyboard[idx];
          storyboard[idx] = storyboard[idx - 1];
          storyboard[idx - 1] = temp;
          projectManager.saveDraft(state.currentProject);
          updateStoryboardDirector();
          renderStoryboardShots();
        }
      });

      card.querySelector('.btn-shot-down').addEventListener('click', () => {
        if (idx < storyboard.length - 1) {
          const temp = storyboard[idx];
          storyboard[idx] = storyboard[idx + 1];
          storyboard[idx + 1] = temp;
          projectManager.saveDraft(state.currentProject);
          updateStoryboardDirector();
          renderStoryboardShots();
        }
      });

      card.querySelector('.btn-shot-clone').addEventListener('click', () => {
        const clone = JSON.parse(JSON.stringify(shot));
        clone.id = 'shot_' + Date.now();
        clone.title = `${shot.title} (Bản sao)`;
        storyboard.splice(idx + 1, 0, clone);
        projectManager.saveDraft(state.currentProject);
        updateStoryboardDirector();
        renderStoryboardShots();
      });

      card.querySelector('.btn-shot-del').addEventListener('click', () => {
        if (storyboard.length > 1) {
          storyboard.splice(idx, 1);
          projectManager.saveDraft(state.currentProject);
          updateStoryboardDirector();
          renderStoryboardShots();
        }
      });

      card.addEventListener('click', (e) => {
        if (['INPUT', 'SELECT', 'TEXTAREA', 'BUTTON'].includes(e.target.tagName)) return;
        previewShot(idx);
      });

      container.appendChild(card);
    });
  }

  function loadAndApplyProject(projectData, _sourceName = '') {
    if (!projectData) return false;
    state.currentProject = projectData;
    if (Array.isArray(state.currentProject.characters)) {
      state.currentProject.characters.forEach((char) => {
        if (typeof char.name !== 'string') char.name = 'Nhân Vật';
        characterManager.saveCharacter(char);
      });
    }
    if (Array.isArray(state.currentProject.storyboard)) {
      state.currentProject.storyboard.forEach((shot, i) => {
        const d = Number(shot.duration);
        shot.duration = Number.isFinite(d) && d > 0 ? d : 3;
        if (typeof shot.title !== 'string') shot.title = `Phân cảnh ${i + 1}`;
        if (typeof shot.dialogue !== 'string') shot.dialogue = '';
      });
    }
    if (state.currentProject.environment?.timeOfDay) {
      setTimeOfDay(state.currentProject.environment.timeOfDay, state);
    }
    const titleIn = document.getElementById('input-project-title');
    if (titleIn && state.currentProject.title) {
      titleIn.value = state.currentProject.title;
    }
    // F-4: Khởi tạo danh sách thú cưng nếu có trong dự án
    spawnProjectPets(state.currentProject.pets, state);

    renderCharacterRoster();
    renderStoryboardShots();
    updateStoryboardDirector();
    return true;
  }

  // --- ATTACH LISTENERS ---
  document.getElementById('btn-save-current-char')?.addEventListener('click', () => {
    if (!state.mainAvatar) return;
    const nameInput = document.getElementById('input-current-char-name');
    const charName = nameInput?.value?.trim() || 'Nhân Vật Tùy Biến';
    const profile = characterManager.extractFromAvatar(state.mainAvatar, charName);
    characterManager.saveCharacter(profile);
    defaultSfx.playTactileFeedback(640);
    renderCharacterRoster();
    alert(`Đã lưu nhân vật "${charName}" vào Kho Nhân Vật thành công!`);
  });

  document.getElementById('btn-create-new-char')?.addEventListener('click', () => {
    state.currentPresetKey = 'TEACHER';
    if (typeof handlers.setupStudioAvatar === 'function') {
      handlers.setupStudioAvatar('TEACHER', { bodyType: 'normal' });
    }
    if (typeof handlers.syncCustomizerChips === 'function') {
      handlers.syncCustomizerChips(AVATAR_PRESETS.TEACHER);
    }
    const nameIn = document.getElementById('input-current-char-name');
    if (nameIn) nameIn.value = 'Nhân Vật Mới';
    if (typeof handlers.switchStudioPanel === 'function') {
      handlers.switchStudioPanel('customizer');
    }
  });

  document.getElementById('btn-roster-random')?.addEventListener('click', () => {
    document.getElementById('btn-randomize')?.click();
    const randNames = ['Nữ Sinh Anime', 'Hiệp Khách Phố Cổ', 'Bác Trà Đá Vui Tính', 'Ninja Vỉa Hè', 'Ca Sĩ Trẻ'];
    const pickedName = randNames[Math.floor(Math.random() * randNames.length)] + ' #' + Math.floor(Math.random() * 100);
    const nameIn = document.getElementById('input-current-char-name');
    if (nameIn) nameIn.value = pickedName;
    if (state.mainAvatar) {
      const profile = characterManager.extractFromAvatar(state.mainAvatar, pickedName);
      characterManager.saveCharacter(profile);
      renderCharacterRoster();
    }
  });

  initProjectActionListeners(state, handlers, { loadAndApplyProject });

  document.getElementById('btn-add-new-shot')?.addEventListener('click', () => {
    const allChars = characterManager.getAllCharacters();
    const firstChar = allChars[0] || { id: 'char_teacher', name: 'Cô Giáo Lan' };
    const newShot = {
      id: 'shot_' + Date.now(),
      title: `Phân cảnh ${(state.currentProject.storyboard?.length || 0) + 1}`,
      duration: 3.5,
      actorId: firstChar.id,
      actorName: firstChar.name,
      spawnPoint: 'sidewalk_center',
      action: 'talking',
      expression: 'happy',
      emotionFx: 'none',
      dialogue: 'Đây là câu thoại mới trong kịch bản!',
      voice: { pitch: 1.0, rate: 1.0, gender: 'female' },
      camera: { preset: 'medium', smooth: true, shake: 0 }
    };
    if (!state.currentProject.storyboard) state.currentProject.storyboard = [];
    state.currentProject.storyboard.push(newShot);
    projectManager.saveDraft(state.currentProject);
    updateStoryboardDirector();
    renderStoryboardShots();
  });

  document.querySelectorAll('#group-time-of-day .chip-btn').forEach((btn) => {
    btn.addEventListener('click', () => {
      setTimeOfDay(btn.dataset.time, state);
    });
  });

  return {
    renderCharacterRoster,
    renderStoryboardShots,
    updateStoryboardDirector,
    previewShot,
    loadAndApplyProject
  };
}
